import { create } from "zustand";
import { persist } from "zustand/middleware";

import type { MarketScope } from "@/components/tenant/TenantContextProvider";

/**
 * SFWL-004: the tradeable asset universe is now backend-owned (`useAssets`).
 * `MarketId` is the asset's `symbol`, which is plain string at runtime — the
 * frozen union previously used here drifted out of sync with the seeded set
 * (e.g. YM/RTY/CL never made it in). Validation now happens at the API
 * boundary, not at the type system level.
 */
export type MarketId = string;
export type Timeframe = "1m" | "5m" | "15m" | "1h" | "4h" | "1d" | "1w" | "1M";

export type IndicatorType = "SMA" | "EMA";

export interface IndicatorConfig {
  id: string;
  type: IndicatorType;
  period: number;
  color: string;
  visible: boolean;
}

/**
 * PR #158 review (jmusterman): the "default market" isn't a single global
 * value — it depends on which asset class the active tenant is scoped to.
 * A crypto-only tenant should default to BTC/USD; a futures-only tenant
 * (e.g. Sweet Futures) should default to ES/USD, the seeded CME E-mini S&P
 * 500 future (confirmed against `api/src/database/seeds/assets.seed.ts`).
 * There is no "equities" MarketScope yet — when one lands, its default
 * (SPY) belongs here; today `MarketScope` covers "futures" | "crypto" | "all".
 */
export const DEFAULT_MARKET_BY_SCOPE: Record<MarketScope, MarketId> = {
  crypto: "BTC/USD",
  futures: "ES/USD",
  all: "BTC/USD",
};

// Used for the store's initial value and the sanitize-only persist
// `migrate` fallback (FEAT-003 AC4). Tenant scope is unknown at both of
// those points — hydration/module-init happens before `useAssets`/`useTenant`
// have resolved anything — so this intentionally stays the "all" scope
// default rather than trying to guess a scope here. The scope-aware default
// is applied later by MarketSelector's default-select effect once the real
// asset universe (and its scope) is known.
const DEFAULT_MARKET: MarketId = DEFAULT_MARKET_BY_SCOPE.all;
const DEFAULT_TIMEFRAME: Timeframe = "1h";

/** Shape of the `partialize`d slice this store persists to localStorage. */
interface PersistedTradeState {
  market: MarketId;
  timeframe: Timeframe;
  indicators: IndicatorConfig[];
}

interface TradeState {
  market: MarketId;
  timeframe: Timeframe;
  indicators: IndicatorConfig[];
  livePrices: Partial<Record<MarketId, number>>;
  setMarket: (market: MarketId) => void;
  setTimeframe: (timeframe: Timeframe) => void;
  addIndicator: (config: IndicatorConfig) => void;
  toggleIndicatorVisibility: (id: string) => void;
  clearIndicators: () => void;
  setLivePrices: (prices: Partial<Record<MarketId, number>>) => void;
}

export const useTradeStore = create<TradeState>()(
  persist(
    (set) => ({
      market: DEFAULT_MARKET,
      timeframe: DEFAULT_TIMEFRAME,
      indicators: [],
      livePrices: {},
      setMarket: (market) => set({ market }),
      setTimeframe: (timeframe) => set({ timeframe }),
      addIndicator: (config) =>
        set((state) => ({
          indicators: [...state.indicators.filter((i) => i.id !== config.id), config],
        })),
      toggleIndicatorVisibility: (id) =>
        set((state) => ({
          indicators: state.indicators.map((ind) =>
            ind.id === id ? { ...ind, visible: !ind.visible } : ind
          ),
        })),
      clearIndicators: () => set({ indicators: [] }),
      setLivePrices: (prices) =>
        set((state) => ({ livePrices: { ...state.livePrices, ...prices } })),
    }),
    {
      name: "trade-store",
      version: 1,
      // FEAT-003 (docs/features/FEAT-003-trade-route-unmapped-market-crash.md):
      // this store previously persisted `market` with no version/migrate.
      // AC4 (amended post-lock, PO-approved): migrate is sanitize-only. It
      // resets `market` to the default ONLY when the persisted value is
      // malformed (not a non-empty string) — e.g. null, a number, or "".
      // A valid-but-unmapped symbol (e.g. CL/USD, no MARKET_CONFIG entry)
      // is a legitimate futures-tenant selection and survives migration
      // untouched: it's already crash-safe via the null-safe Hyperliquid
      // lookup (AC1) and MarketSelector's own in-universe re-default.
      // Every other persisted field passes through unchanged.
      migrate: (persistedState): PersistedTradeState => {
        const state = persistedState as Partial<PersistedTradeState> | undefined;
        const market =
          typeof state?.market === "string" && state.market.length > 0
            ? state.market
            : DEFAULT_MARKET;
        return {
          market,
          timeframe: state?.timeframe ?? DEFAULT_TIMEFRAME,
          indicators: state?.indicators ?? [],
        };
      },
      partialize: (state) => ({
        market: state.market,
        timeframe: state.timeframe,
        indicators: state.indicators,
      }),
    }
  )
);
